import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTourAutoStart } from '@/hooks/useTourAutoStart';
import { customerOrderDetailTourId, customerOrderDetailSteps } from '@/tours/customer';
import { ArrowLeft, Calendar, MapPin, Hash, Package } from 'lucide-react';
import { ordersApi } from '@/services/api/ordersApi';
import { queryKeys } from '@/services/api/queryClient';
import { Card, Badge, LoadingSpinner, ErrorMessage } from '@/components/ui';
import QRCodeDisplay from '@/components/features/customer/QRCodeDisplay';
import StatusTimeline from '@/components/features/customer/StatusTimeline';
import { cn } from '@/lib/utils';

const OrderDetailPage: React.FC = () => {
  useTourAutoStart({ tourId: customerOrderDetailTourId, steps: customerOrderDetailSteps });
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const orderId = Number(id);

  const { data: order, isLoading, isError, refetch } = useQuery({
    queryKey: queryKeys.myOrder(orderId),
    queryFn: () => ordersApi.getMyOrderById(orderId),
    enabled: !Number.isNaN(orderId),
  });

  if (isLoading) {
    return (
      <div className="py-20">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (isError || !order) {
    return (
      <ErrorMessage
        message="Не удалось загрузить информацию о заказе"
        onRetry={() => refetch()}
      />
    );
  }

  return (
    <div>
      <button
        onClick={() => navigate('/my-orders')}
        className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 transition-colors mb-4"
      >
        <ArrowLeft className="w-4 h-4" />
        Назад к заказам
      </button>

      <div className="flex items-start justify-between gap-3 mb-6">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-bold text-gray-900">
              Заказ {order.order_number}
            </h1>
            <Badge.Status status={order.status} />
          </div>
          <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
            <span className="flex items-center gap-1">
              <Calendar className="w-3.5 h-3.5" />
              {new Date(order.created_at).toLocaleDateString('ru-RU', {
                day: 'numeric',
                month: 'long',
                year: 'numeric',
              })}
            </span>
            {order.shelf_location && (
              <span className="flex items-center gap-1">
                <MapPin className="w-3.5 h-3.5" />
                Полка {order.shelf_location}
              </span>
            )}
          </div>
        </div>
      </div>

      <div data-tour="customer:order-timeline" className="mb-5">
        <Card>
          <Card.Header>
            <h2 className="text-sm font-semibold text-gray-900">Статус заказа</h2>
          </Card.Header>
          <Card.Body>
            <StatusTimeline currentStatus={order.status} />
          </Card.Body>
        </Card>
      </div>

      <div data-tour="customer:order-qr" className="mb-5">
        <Card>
          <Card.Header>
            <h2 className="text-sm font-semibold text-gray-900">QR-код для получения</h2>
          </Card.Header>
          <Card.Body className="flex justify-center">
            <QRCodeDisplay orderId={order.id} orderNumber={order.order_number} />
          </Card.Body>
        </Card>
      </div>

      <Card>
        <Card.Header>
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-gray-900">Товары</h2>
            <span className="text-xs text-gray-400">
              {order.items.length} {pluralizeItems(order.items.length)}
            </span>
          </div>
        </Card.Header>
        <Card.Body className="p-0">
          {order.items.length === 0 ? (
            <div className="px-6 py-8 text-center text-sm text-gray-400">
              Нет информации о товарах
            </div>
          ) : (
            <div className="divide-y divide-gray-50">
              {order.items.map(item => {
                const shortage = item.quantity_received < item.quantity_ordered;
                return (
                  <div
                    key={item.id}
                    className={cn(
                      'px-6 py-4 flex items-center gap-4',
                      shortage && 'bg-red-50/40',
                    )}
                  >
                    <div className="w-10 h-10 rounded-xl bg-gray-100 flex items-center justify-center shrink-0">
                      <Package className="w-5 h-5 text-gray-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {item.name}
                      </p>
                      {item.sku && (
                        <p className="text-xs text-gray-400 flex items-center gap-1 mt-0.5">
                          <Hash className="w-3 h-3" />
                          {item.sku}
                        </p>
                      )}
                    </div>
                    <div className="text-right shrink-0">
                      <p className={cn(
                        'text-sm font-semibold',
                        shortage ? 'text-red-600' : 'text-gray-900',
                      )}>
                        {item.quantity_handed_off ?? item.quantity_received} / {item.quantity_ordered}
                      </p>
                      <p className="text-[11px] text-gray-400">выдано / заказано</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card.Body>
      </Card>

      {order.notes && (
        <Card className="mt-5">
          <Card.Body>
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Примечания</p>
            <p className="text-sm text-gray-700">{order.notes}</p>
          </Card.Body>
        </Card>
      )}
    </div>
  );
};

function pluralizeItems(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return 'позиция';
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return 'позиции';
  return 'позиций';
}

export default OrderDetailPage;
